import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { TILE_SIZE, CHUNK_SIZE, getChunkRegion } from '../constants';
import SocketCacheSync from '../../../services/SocketCacheSync';
import type { ShipData } from '../types';
import type { WorldEvent } from '../../WorldEventNotification';
import type { OceanScene } from '../../../game/scenes/OceanScene';
import { getOrCreateGuestId } from '../../../utils/guestSession';

interface UseGameCanvasSocketProps {
  socketRef: React.MutableRefObject<Socket | null>;
  gameRef: React.MutableRefObject<Phaser.Game | null>;
  playerInfo: {
    name: string;
    character: any;
    shipColor: string;
    characterId?: string;
    accountId?: string;
    token?: string;
    savedPosition?: { x: number; y: number };
    savedStats?: { berries: number; level: number; xp: number; infamy: number };
    savedSkills?: any;
    startingOcean?: string;
  };
  // State setters
  setConnected: (connected: boolean) => void;
  setPlayerId: (id: string | undefined) => void;
  setSocketForHotbar: (socket: Socket) => void;
  setShipCount: (count: number) => void;
  setPosition: (pos: { x: number; y: number }) => void;
  setTilePos: (pos: { x: number; y: number }) => void;
  setChunkPos: (pos: { x: number; y: number }) => void;
  setPlayerHp: (hp: { hull: number; maxHull: number; sail: number; maxSail: number }) => void;
  setPlayerSP: (sp: { current: number; max: number }) => void;
  setInCombat: (inCombat: boolean) => void;
  setPieces: (pieces: number) => void;
  setXp: (xp: number) => void;
  setLevel: (level: number) => void;
  setInfamy: (infamy: number) => void;
  setSecurityTier: (tier: 'safe_zone' | 'contested' | 'open_waters') => void;
  setShipId: (id: string) => void;
  setShipType: (type: string) => void;
  setCurrentRegion: (region: string) => void;
  setIsDocked: (docked: boolean) => void;
  setDockedIslandId: (id: string | null) => void;
  setUnlockedBlueprints: React.Dispatch<React.SetStateAction<Set<string>>>;
  setHasEssenceEquipped: (has: boolean) => void;
  setSeaMonsters: React.Dispatch<React.SetStateAction<Array<{ id: string; type: string; x: number; y: number }>>>;
  setFactionReputation: React.Dispatch<React.SetStateAction<{ navy: number; pirate: number; merchant: number; smuggler: number }>>;
  setFactionTiers: (tiers: {
    navy: { tier: string; name: string; color: string };
    pirate: { tier: string; name: string; color: string };
    merchant: { tier: string; name: string; color: string };
    smuggler: { tier: string; name: string; color: string };
  }) => void;
  setFactionDailyGains: (gains: { navy: number; pirate: number; merchant: number; smuggler: number }) => void;
  setFactionDailyCaps: (caps: { navy: number; pirate: number; merchant: number; smuggler: number }) => void;
  setResearchRefreshNonce: React.Dispatch<React.SetStateAction<number>>;
  setKickMessage: (message: string | null) => void;
  setApiBaseUrl: (url: string) => void;
  setIsDisconnected: (disconnected: boolean) => void;
  setReconnectAttempt: (attempt: number) => void;
  setDuplicateConnectionError: (error: boolean) => void;
  setCurrentWeather: (weather: 'clear' | 'fog' | 'storm' | 'calm') => void;
  setLoadingOverlayVisible: (visible: boolean) => void;
  setLoadingOverlayMessage: (message: string) => void;
  setIsInstanceEntry: (isInstance: boolean) => void;
  // Refs
  isDockedRef: React.MutableRefObject<boolean>;
  dockedIslandIdRef: React.MutableRefObject<string | null>;
  researchIslandIdRef: React.MutableRefObject<string | null>;
  researchPanelOpenRef: React.MutableRefObject<boolean>;
  hasReceivedLocalShipRef: React.MutableRefObject<boolean>;
  phaserReadyRef: React.MutableRefObject<boolean>;
  // Callbacks
  callReadyIfPossible: () => void;
  enqueueToast: (text: string, type?: 'error' | 'warning' | 'success') => void;
  addWorldEvent: (event: Omit<WorldEvent, 'id' | 'timestamp'>) => void;
  getRaidTitle: (islandName: string, tier: string) => string;
  getRaidFlavorText: (tier: string) => string;
}

/**
 * Hook to manage all socket event handlers for GameCanvas
 */
export function useGameCanvasSocket(props: UseGameCanvasSocketProps): void {
  const {
    socketRef,
    gameRef,
    playerInfo,
    setConnected,
    setPlayerId,
    setSocketForHotbar,
    setShipCount,
    setPosition,
    setTilePos,
    setChunkPos,
    setPlayerHp,
    setPlayerSP,
    setInCombat,
    setPieces,
    setXp,
    setLevel,
    setInfamy,
    setSecurityTier,
    setShipId,
    setShipType,
    setCurrentRegion,
    setIsDocked,
    setDockedIslandId,
    setUnlockedBlueprints,
    setHasEssenceEquipped,
    setSeaMonsters,
    setFactionReputation,
    setFactionTiers,
    setFactionDailyGains,
    setFactionDailyCaps,
    setResearchRefreshNonce,
    setKickMessage,
    setApiBaseUrl,
    setIsDisconnected,
    setReconnectAttempt,
    setDuplicateConnectionError,
    setCurrentWeather,
    setLoadingOverlayVisible,
    setLoadingOverlayMessage,
    setIsInstanceEntry,
    isDockedRef,
    dockedIslandIdRef,
    researchIslandIdRef,
    researchPanelOpenRef,
    hasReceivedLocalShipRef,
    phaserReadyRef,
    callReadyIfPossible,
    enqueueToast,
    addWorldEvent,
    getRaidTitle,
    getRaidFlavorText,
  } = props;

  // Store handlers for cleanup
  const handlersRef = useRef<Array<{ event: string; handler: (...args: any[]) => void }>>([]);
  const wasConnectedRef = useRef<boolean>(false);
  const initialPlayerInfoRef = useRef<typeof playerInfo>(playerInfo);

  useEffect(() => {
    const serverUrl: string = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setApiBaseUrl(serverUrl);
    socketRef.current = io(serverUrl, {
      // Connection timeout settings (must match server)
      timeout: 60000,           // 60 seconds - connection timeout
      reconnection: true,        // Enable automatic reconnection
      reconnectionDelay: 1000,   // 1 second - initial delay before reconnection
      reconnectionDelayMax: 5000, // 5 seconds - max delay between reconnection attempts
      reconnectionAttempts: Infinity, // Unlimited reconnection attempts
      transports: ['websocket', 'polling'], // Prefer websocket, fallback to polling
    });
    
    // Expose socket to window immediately for standalone panels
    (window as any).gameSocket = socketRef.current;
    console.log('✅ Socket created and exposed to window.gameSocket');

    // Connect handler
    const handleConnect = () => {
      // Check if this is a reconnect by checking if we were connected before
      const wasReconnect = wasConnectedRef.current;
      setConnected(true);
      setIsDisconnected(false);
      setReconnectAttempt(0);
      wasConnectedRef.current = true;
      const socket = socketRef.current;
      if (!socket) {
        console.error('❌ Socket ref is null on connect!');
        return;
      }
      const newPlayerId = socket.id;
      console.log(`🔌 Socket connected! playerId: ${newPlayerId}, socket.connected: ${socket.connected}`);
      if (!newPlayerId) {
        console.error('❌ Socket ID is undefined on connect!');
      }
      setPlayerId(newPlayerId);
      setSocketForHotbar(socket);
      
      // Show reconnection toast if this is a reconnect (not initial connect)
      if (wasReconnect) {
        const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
        if (scene && scene.sys.isActive()) {
          console.log('🔌 Showing reconnection message');
          scene.showFloatingMessage('Connected to server', '#44ff44');
          // Clear any stale ships from the previous connection to prevent duplicates
          scene.clearLocalShip();
        }
      }
      
      // Initialize real-time cache synchronization
      SocketCacheSync.getInstance().initialize({
        socket,
        enableWorldSync: true,
        enablePlayerSync: true,
        enableEntitySync: true,
        enableMarketSync: true,
      });
      
      socket.emit('playerJoin', {
        name: initialPlayerInfoRef.current.name,
        character: initialPlayerInfoRef.current.character,
        shipColor: initialPlayerInfoRef.current.shipColor,
        characterId: initialPlayerInfoRef.current.characterId,
        accountId: initialPlayerInfoRef.current.accountId,
        token: initialPlayerInfoRef.current.token,
        guestId: getOrCreateGuestId(),
        savedPosition: initialPlayerInfoRef.current.savedPosition,
        savedStats: initialPlayerInfoRef.current.savedStats,
        savedSkills: initialPlayerInfoRef.current.savedSkills,
        startingOcean: initialPlayerInfoRef.current.startingOcean,
      });
      
      // Update scene with socket after connection
      const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
      if (scene && socketRef.current && socketRef.current.id) {
        console.log(`🔌 Socket connected, updating scene with socket id: ${socketRef.current.id}`);
        scene.setSocket(socketRef.current);
        scene.setLocalPlayerId(socketRef.current.id);
        if (initialPlayerInfoRef.current.characterId) {
          scene.setLocalCharacterId(initialPlayerInfoRef.current.characterId);
        }
      }
    };

    // Disconnect handler
    const handleDisconnect = (reason: string) => {
      console.error(`🔌 Socket disconnected! Reason: ${reason}`);
      wasConnectedRef.current = false;
      setConnected(false);
      setPlayerId(undefined);
      setIsDisconnected(true);
      setReconnectAttempt(0);
      
      // Clean up local ship to prevent ghost players
      const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
      if (scene && scene.sys.isActive()) {
        scene.clearLocalShip();
        
        let message = 'Disconnected from server';
        if (reason === 'io server disconnect') {
          message = 'Server closed connection';
        } else if (reason === 'transport close') {
          message = 'Connection lost';
        } else if (reason === 'ping timeout') {
          message = 'Connection timeout';
        }
        console.log(`🔌 Disconnect reason: ${message}`);
        // Don't show floating message - DisconnectionOverlay handles the UI
      } else {
        console.warn('🔌 Scene not available for disconnect message');
      }
    };

    // Admin kick handler
    const handleAdminKick = (data: { reason?: string }) => {
      const reason = typeof data?.reason === 'string' && data.reason.trim().length > 0
        ? data.reason
        : 'Disconnected by moderator';
      setKickMessage(reason);
      try {
        socketRef.current?.disconnect();
      } catch {
        // ignore
      }
    };

    // Ships update handler
    const handleShipsUpdate = (data: { ships: Array<ShipData & { ownerId: string }> }) => {
      // Defensive check - ensure ships array exists
      if (!data || !data.ships || !Array.isArray(data.ships)) {
        console.error('Invalid ships:update data received:', data);
        return;
      }
      
      setShipCount(data.ships.length);
      const localShip = data.ships.find(s => s.ownerId === socketRef.current?.id);
      if (localShip) {
        hasReceivedLocalShipRef.current = true;
        callReadyIfPossible();
        const newX = Math.round(localShip.x);
        const newY = Math.round(localShip.y);
        const newTileX = Math.floor(localShip.x / TILE_SIZE);
        const newTileY = Math.floor(localShip.y / TILE_SIZE);
        const newChunkX = Math.floor(localShip.x / CHUNK_SIZE);
        const newChunkY = Math.floor(localShip.y / CHUNK_SIZE);
        
        setPosition({ x: newX, y: newY });
        setTilePos({ x: newTileX, y: newTileY });
        setChunkPos({ x: newChunkX, y: newChunkY });
        
        // Auto-dismiss docked panel if player moves too far from docked island
        if (isDockedRef.current && dockedIslandIdRef.current) {
          const scene = gameRef.current?.scene.getScene('OceanScene') as any;
          if (scene && scene.islandRenderer) {
            const islands = scene.islandRenderer.getIslands?.();
            if (islands && islands instanceof Map) {
              const dockedIslandEntry = islands.get(dockedIslandIdRef.current);
              if (dockedIslandEntry && dockedIslandEntry.islandData) {
                const dockedIsland = dockedIslandEntry.islandData;
                const distToIsland = Math.hypot(localShip.x - dockedIsland.x, localShip.y - dockedIsland.y);
                const DOCKED_PANEL_DISMISS_DISTANCE = TILE_SIZE * 20; // 20 tiles away
                
                if (distToIsland > DOCKED_PANEL_DISMISS_DISTANCE) {
                  // Player moved too far - dismiss docked panel
                }
              }
            }
          }
        }
        
        // Update HP
        setPlayerHp({
          hull: localShip.hullHp,
          maxHull: localShip.maxHullHp,
          sail: localShip.sailHp || 50,
          maxSail: localShip.maxSailHp || 50,
        });

        // Update SP (Stamina Points)
        setPlayerSP({
          current: localShip.currentSP || 100,
          max: localShip.maxSP || 100,
        });
        
        // Update combat state
        setInCombat(localShip.combatState === 'combat');

        // Docked state sync from server - only used as fallback for reconnect scenarios
        const isActuallyDocked: boolean = localShip.combatState === 'docked' && typeof localShip.dockedAt === 'string' && localShip.dockedAt.length > 0;
        if (isActuallyDocked && !isDockedRef.current) {
          setIsDocked(true);
          if (typeof localShip.dockedAt === 'string' && localShip.dockedAt.length > 0) {
            setDockedIslandId(localShip.dockedAt);
          }
        }
        
        // Update pieces and progression
        setPieces(localShip.pieces || 0);
        setXp(localShip.xp || 0);
        setLevel(localShip.level || 1);
        setInfamy(localShip.infamy || 0);

        if ((localShip as unknown as { securityTier?: 'safe_zone' | 'contested' | 'open_waters' }).securityTier) {
          setSecurityTier((localShip as unknown as { securityTier: 'safe_zone' | 'contested' | 'open_waters' }).securityTier);
        }
        
        // Update ship data for fitting panel
        setShipId(localShip.id);
        if ((localShip as any).shipType) {
          setShipType((localShip as any).shipType);
        }
        
        // Determine region based on chunk position (matches mini-map)
        const chunkX = Math.floor(localShip.x / CHUNK_SIZE);
        const chunkY = Math.floor(localShip.y / CHUNK_SIZE);
        const region = getChunkRegion(chunkX, chunkY);
        if (region) {
          setCurrentRegion(region.id);
        }
      }
    };

    // Loot gained handler
    const handleLootGained = (data: { newTotalPieces?: number; newTotalXp?: number; newLevel?: number }) => {
      if (typeof data.newTotalPieces === 'number') {
        setPieces(data.newTotalPieces);
      }
      if (typeof data.newTotalXp === 'number') {
        setXp(data.newTotalXp);
      }
      if (typeof data.newLevel === 'number') {
        setLevel(data.newLevel);
      }
    };

    // Ship pieces handler
    const handleShipPieces = (data: { pieces: number }) => {
      if (typeof data.pieces === 'number') {
        setPieces(data.pieces);
      }
    };

    // Inventory buy result handler
    const handleInventoryBuyResult = (data: { newBalance?: number }) => {
      if (typeof data.newBalance === 'number') {
        setPieces(data.newBalance);
      }
    };

    // Inventory sell result handler
    const handleInventorySellResult = (data: { newBalance?: number }) => {
      if (typeof data.newBalance === 'number') {
        setPieces(data.newBalance);
      }
    };

    // Signature list handler
    const handleSignatureList = (data: { moves?: Array<{ definitionId: string }>; equipped?: string | null }) => {
      const hasMoves = Array.isArray(data.moves) && data.moves.length > 0;
      const hasEquipped = !!data.equipped;
      setHasEssenceEquipped(hasMoves || hasEquipped);
    };

    // Signature unlocked handler
    const handleSignatureUnlocked = () => {
      setHasEssenceEquipped(true);
    };

    // Crafting consume blueprint result handler
    const handleCraftingConsumeBlueprint = (data: { success: boolean; recipeId?: string }) => {
      if (data.success && data.recipeId) {
        setUnlockedBlueprints((prev) => {
          const next = new Set<string>(prev);
          next.add(data.recipeId as string);
          return next;
        });
      }
    };

    // Resource error handler
    const handleResourceError = (data: { message: string; type?: string }) => {
      enqueueToast(
        data.message,
        data.type === 'inventory_full' ? 'warning' : 'error',
      );
    };

    // Territory update handler
    const handleTerritoryUpdate = (data: { islandId: string }): void => {
      if (researchPanelOpenRef.current && data.islandId && data.islandId === researchIslandIdRef.current) {
        setResearchRefreshNonce((prev) => prev + 1);
      }
    };

    // Cache invalidate handler
    const handleCacheInvalidate = (data: { type: string; id?: string }): void => {
      if (data.type === 'island' && data.id) {
        const { clearResearchedIsland } = require('../../IslandResearchPanel');
        clearResearchedIsland(data.id);
        if (researchPanelOpenRef.current && data.id === researchIslandIdRef.current) {
          setResearchRefreshNonce((prev) => prev + 1);
        }
      } else if (data.type === 'all') {
        const { clearAllResearchedIslands } = require('../../IslandResearchPanel');
        clearAllResearchedIslands();
        if (researchPanelOpenRef.current) {
          setResearchRefreshNonce((prev) => prev + 1);
        }
      }
    };

    // Reputation update handler (legacy)
    const handleReputationUpdate = (data: { factionReputation: { navy: number; pirate: number; merchant: number } }) => {
      setFactionReputation(prev => ({
        ...prev,
        navy: data.factionReputation.navy,
        pirate: data.factionReputation.pirate,
        merchant: data.factionReputation.merchant,
      }));
    };

    // Faction update handler
    const handleFactionUpdate = (data: {
      reputation: { navy: number; pirate: number; merchant: number; smuggler: number };
      tiers: {
        navy: { tier: string; name: string; color: string };
        pirate: { tier: string; name: string; color: string };
        merchant: { tier: string; name: string; color: string };
        smuggler: { tier: string; name: string; color: string };
      };
      dailyGains: { navy: number; pirate: number; merchant: number; smuggler: number };
      dailyCaps: { navy: number; pirate: number; merchant: number; smuggler: number };
    }) => {
      setFactionReputation(data.reputation);
      setFactionTiers(data.tiers);
      setFactionDailyGains(data.dailyGains);
      setFactionDailyCaps(data.dailyCaps);
    };

    // Weather changed handler
    const handleWeatherChanged = (data: { weather: string; duration: number; effects: Record<string, number> }) => {
      const weatherNames: Record<string, string> = {
        clear: 'Clear Skies',
        fog: 'Dense Fog',
        storm: 'Raging Storm',
        calm: 'Calm Waters',
      };
      const weatherDescriptions: Record<string, string> = {
        clear: 'Visibility restored',
        fog: 'Visibility reduced',
        storm: 'Speed reduced, visibility limited',
        calm: 'Fishing bonus active',
      };
      
      // Update current weather state
      if (['clear', 'fog', 'storm', 'calm'].includes(data.weather)) {
        setCurrentWeather(data.weather as 'clear' | 'fog' | 'storm' | 'calm');
      }
      
      addWorldEvent({
        type: 'weather',
        title: weatherNames[data.weather] || 'Weather Change',
        description: weatherDescriptions[data.weather] || 'Weather has changed',
        severity: data.weather === 'storm' ? 'major' : data.weather === 'fog' ? 'moderate' : 'minor',
      });
    };

    // Raid started handler
    const handleRaidStarted = (data: { islandName: string; tier: string }) => {
      addWorldEvent({
        type: 'pirate_raid',
        title: getRaidTitle(data.islandName, data.tier),
        description: getRaidFlavorText(data.tier),
        severity: data.tier === 'invasion' ? 'catastrophic' : data.tier === 'raid' ? 'major' : 'moderate',
      });
    };

    // Rift storm spawned handler
    const handleRiftStormSpawned = (data: { tier: string; x: number; y: number }) => {
      addWorldEvent({
        type: 'rift_storm',
        title: `RIFT STORM - ${data.tier.toUpperCase()}`,
        description: `Dangerous corrupted rift detected at coordinates`,
        severity: data.tier as 'minor' | 'moderate' | 'major' | 'catastrophic',
        location: { x: data.x, y: data.y },
      });
    };

    // Sea monster spawned handler - handles both world event format and position format
    const handleSeaMonsterSpawned = (data: { type?: string; description?: string; id?: string; x?: number; y?: number }) => {
      // Check if this is the world event format (has description)
      if (data.description !== undefined) {
        addWorldEvent({
          type: 'sea_monster',
          title: `SEA MONSTER SIGHTING`,
          description: data.description || 'Something stirs in the deep',
          severity: 'major',
        });
      }
      // Check if this is the position format (has id, x, y)
      if (data.id !== undefined && data.x !== undefined && data.y !== undefined) {
        setSeaMonsters(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          return [...prev, { id: data.id!, type: data.type || 'unknown', x: data.x!, y: data.y! }];
        });
      }
    };

    // Sea monster list handler
    const handleSeaMonsterList = (monsters: Array<{ id: string; type: string; x: number; y: number }>) => {
      setSeaMonsters(monsters);
    };

    // Sea monster despawned handler
    const handleSeaMonsterDespawned = (data: { id: string }) => {
      setSeaMonsters(prev => prev.filter(m => m.id !== data.id));
    };

    // Sea monster positions handler
    const handleSeaMonsterPositions = (data: { monsters: Array<{ id: string; type: string; x: number; y: number }> }) => {
      setSeaMonsters(data.monsters);
    };

    // Territory claimed handler
    const handleTerritoryClaimed = (data: { islandName: string; crewName: string; tier: string }) => {
      addWorldEvent({
        type: 'territory_claim',
        title: `TERRITORY CLAIMED`,
        description: `${data.crewName} claimed ${data.islandName}`,
        severity: 'moderate',
      });
    };

    // Crew formed handler
    const handleCrewFormed = (data: { crewName: string; captainName: string }) => {
      addWorldEvent({
        type: 'crew_event',
        title: `NEW CREW FORMED`,
        description: `${data.crewName} set sail under ${data.captainName}`,
        severity: 'minor',
      });
    };

    // Crew disbanded handler
    const handleCrewDisbanded = (data: { crewName: string }) => {
      addWorldEvent({
        type: 'crew_event',
        title: `CREW DISBANDED`,
        description: `${data.crewName} has disbanded`,
        severity: 'minor',
      });
    };

    // Dock services result handler
    const handleDockServicesResult = (data: { success: boolean; message: string; heat: number }) => {
      if (data.success) {
        // Heat update will be handled by ships:update
        enqueueToast(`Heat reduced! Current heat: ${Math.round(data.heat)}`, 'success');
      } else {
        enqueueToast(data.message, 'error');
      }
    };

    // Health replenishment result handler
    const handleReplenishHealthResult = (data: { success: boolean; message: string; hullHp: number; maxHullHp: number; sailHp: number; maxSailHp: number; cost: number; newBerries: number }) => {
      if (data.success) {
        setPlayerHp({
          hull: data.hullHp,
          maxHull: data.maxHullHp,
          sail: data.sailHp,
          maxSail: data.maxSailHp,
        });
        setPieces(data.newBerries);
        enqueueToast(`Health replenished for ₱${data.cost.toLocaleString()}!`, 'success');
      } else {
        enqueueToast(data.message, 'error');
      }
    };

    // Ship upgrade result handler
    const handleShipUpgradeResult = (data: { success: boolean; message: string; shipType?: string }) => {
      if (data.success) {
        if (data.shipType) {
          setShipType(data.shipType);
        }
        enqueueToast(data.message, 'success');
      } else {
        enqueueToast(data.message, 'error');
      }
    };

    // Register all handlers
    if (socketRef.current) {
      socketRef.current.on('connect', handleConnect);
      handlersRef.current.push({ event: 'connect', handler: handleConnect });

      socketRef.current.emit('wildcard:list');

      socketRef.current.on('disconnect', handleDisconnect);
      handlersRef.current.push({ event: 'disconnect', handler: handleDisconnect });

      socketRef.current.on('admin:kick', handleAdminKick);
      handlersRef.current.push({ event: 'admin:kick', handler: handleAdminKick });

      socketRef.current.on('ships:update', handleShipsUpdate);
      handlersRef.current.push({ event: 'ships:update', handler: handleShipsUpdate });

      socketRef.current.on('loot:gained', handleLootGained);
      handlersRef.current.push({ event: 'loot:gained', handler: handleLootGained });

      socketRef.current.on('ship:pieces', handleShipPieces);
      handlersRef.current.push({ event: 'ship:pieces', handler: handleShipPieces });

      socketRef.current.on('inventory:buy:result', handleInventoryBuyResult);
      handlersRef.current.push({ event: 'inventory:buy:result', handler: handleInventoryBuyResult });

      socketRef.current.on('inventory:sell:result', handleInventorySellResult);
      handlersRef.current.push({ event: 'inventory:sell:result', handler: handleInventorySellResult });

      socketRef.current.on('wildcard:list', handleSignatureList);
      handlersRef.current.push({ event: 'wildcard:list', handler: handleSignatureList });

      socketRef.current.on('wildcard:unlocked', handleSignatureUnlocked);
      handlersRef.current.push({ event: 'wildcard:unlocked', handler: handleSignatureUnlocked });

      socketRef.current.on('crafting:consume_blueprint:result', handleCraftingConsumeBlueprint);
      handlersRef.current.push({ event: 'crafting:consume_blueprint:result', handler: handleCraftingConsumeBlueprint });

      socketRef.current.on('resource:error', handleResourceError);
      handlersRef.current.push({ event: 'resource:error', handler: handleResourceError });

      socketRef.current.on('territory:update', handleTerritoryUpdate);
      handlersRef.current.push({ event: 'territory:update', handler: handleTerritoryUpdate });

      socketRef.current.on('cache:invalidate', handleCacheInvalidate);
      handlersRef.current.push({ event: 'cache:invalidate', handler: handleCacheInvalidate });

      socketRef.current.on('reputation:update', handleReputationUpdate);
      handlersRef.current.push({ event: 'reputation:update', handler: handleReputationUpdate });

      socketRef.current.on('faction:update', handleFactionUpdate);
      handlersRef.current.push({ event: 'faction:update', handler: handleFactionUpdate });

      socketRef.current.on('weather:changed', handleWeatherChanged);
      handlersRef.current.push({ event: 'weather:changed', handler: handleWeatherChanged });

      socketRef.current.on('raid:started', handleRaidStarted);
      handlersRef.current.push({ event: 'raid:started', handler: handleRaidStarted });

      socketRef.current.on('riftStorm:spawned', handleRiftStormSpawned);
      handlersRef.current.push({ event: 'riftStorm:spawned', handler: handleRiftStormSpawned });

      socketRef.current.on('seaMonster:spawned', handleSeaMonsterSpawned);
      handlersRef.current.push({ event: 'seaMonster:spawned', handler: handleSeaMonsterSpawned });

      socketRef.current.on('seaMonster:list', handleSeaMonsterList);
      handlersRef.current.push({ event: 'seaMonster:list', handler: handleSeaMonsterList });

      socketRef.current.on('seaMonster:despawned', handleSeaMonsterDespawned);
      handlersRef.current.push({ event: 'seaMonster:despawned', handler: handleSeaMonsterDespawned });

      socketRef.current.on('seaMonster:positions', handleSeaMonsterPositions);
      handlersRef.current.push({ event: 'seaMonster:positions', handler: handleSeaMonsterPositions });

      socketRef.current.on('territory:claimed', handleTerritoryClaimed);
      handlersRef.current.push({ event: 'territory:claimed', handler: handleTerritoryClaimed });

      socketRef.current.on('crew:formed', handleCrewFormed);
      handlersRef.current.push({ event: 'crew:formed', handler: handleCrewFormed });

      socketRef.current.on('crew:disbanded', handleCrewDisbanded);
      handlersRef.current.push({ event: 'crew:disbanded', handler: handleCrewDisbanded });

      socketRef.current.on('ship:dockServices:result', handleDockServicesResult);
      handlersRef.current.push({ event: 'ship:dockServices:result', handler: handleDockServicesResult });

      socketRef.current.on('ship:replenishHealth:result', handleReplenishHealthResult);
      handlersRef.current.push({ event: 'ship:replenishHealth:result', handler: handleReplenishHealthResult });

      socketRef.current.on('ship:upgrade:result', handleShipUpgradeResult);
      handlersRef.current.push({ event: 'ship:upgrade:result', handler: handleShipUpgradeResult });
      
      // Connection error handlers
      const handleConnectError = (error: Error) => {
        console.error('🔌 Connection error:', error.message);
        const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
        if (scene && scene.sys.isActive()) {
          console.log('🔌 Showing connection error message');
          scene.showFloatingMessage('Connection error - retrying...', '#ffaa00');
        }
      };
      
      const handleReconnectAttempt = (attempt: number) => {
        console.log(`🔌 Reconnection attempt ${attempt}`);
        setReconnectAttempt(attempt);
        if (attempt === 1) {
          const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
          if (scene && scene.sys.isActive()) {
            console.log('🔌 Showing reconnection attempt message');
            scene.showFloatingMessage('Reconnecting...', '#ffaa00');
          }
        }
      };
      
      const handleReconnectFailed = () => {
        console.error('🔌 Reconnection failed');
        const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
        if (scene && scene.sys.isActive()) {
          console.log('🔌 Showing reconnection failed message');
          scene.showFloatingMessage('Could not reconnect to server', '#ff4444');
        }
      };
      
      socketRef.current.on('connect_error', handleConnectError);
      socketRef.current.on('reconnect_attempt', handleReconnectAttempt);
      socketRef.current.on('reconnect_failed', handleReconnectFailed);
      
      // Handle server-sent errors (like duplicate connection)
      socketRef.current.on('error', (data: { message: string }) => {
        console.log('🔌 Server error received:', data.message);
        
        // Check if this is a duplicate connection error
        if (data.message.includes('logged in from another location')) {
          // Show a proper overlay instead of alert
          setDuplicateConnectionError(true);
        } else {
          // For other errors, show floating message
          const scene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;
          if (scene && scene.sys.isActive()) {
            scene.showFloatingMessage(data.message, '#ff4444');
          }
        }
      });

      // Sanctum socket event handlers
      const oceanScene = gameRef.current?.scene.getScene('OceanScene') as OceanScene;

      if (oceanScene && socketRef.current) {
        const handleSanctumInstanceCreated = (data: { instanceId: string; sanctumType: string; sanctumGrade: string; dungeonSeed: number; entryRoomId: string; spawnPosition: any }) => {
          console.log('🏛️ Sanctum instance created:', data.instanceId);
          console.log('🏛️ Ocean scene exists:', !!oceanScene);
          console.log('🏛️ Calling enterSanctum with metadata...');
          oceanScene.enterSanctum(data.instanceId, undefined, data.sanctumType as 'survival' | 'preservation' | 'experiment', data.sanctumGrade as 'A' | 'B' | 'C' | 'D');
          console.log('🏛️ enterSanctum called');
        };

        const handleSanctumCreateAndEnter = () => {
          console.log('🏛️ Sanctum create_and_enter initiated');
          setIsInstanceEntry(true);
          setLoadingOverlayMessage('Creating Sanctum...');
          setLoadingOverlayVisible(true);
          setTimeout(() => setLoadingOverlayVisible(false), 3000);
        };
        
        const handleSanctumRoomData = (data: { room: any }) => {
          console.log('🏛️ Sanctum room data received:', data.room.id, data.room.type);
          console.log('🏛️ Ocean scene:', oceanScene);
          console.log('🏛️ Ocean scene renderSanctumRoom:', oceanScene?.renderSanctumRoom);
          try {
            console.log('🏛️ Calling renderSanctumRoom...');
            oceanScene.renderSanctumRoom(data.room);
            console.log('🏛️ renderSanctumRoom called successfully');
          } catch (error) {
            console.error('🏛️ Error calling renderSanctumRoom:', error);
          }
        };
        
        const handleSanctumEnemyDefeated = (data: { enemyId: string; rewards: any }) => {
          console.log('🏛️ Enemy defeated:', data.enemyId);
          oceanScene.updateSanctumEnemy(data.enemyId, 0, true);
        };
        
        const handleSanctumLootReceived = (data: { containerId: string; loot: any }) => {
          console.log('🏛️ Loot received from container:', data.containerId, data.loot);
          oceanScene.updateSanctumContainer(data.containerId, true);
          
          // Add berries to inventory via server
          if (data.loot?.berries) {
            socketRef.current?.emit('player:addBerries', { amount: data.loot.berries });
            enqueueToast(`+${data.loot.berries} berries`, 'success');
          }
          
          // Add items to inventory (if any)
          if (data.loot?.items && data.loot.items.length > 0) {
            enqueueToast(`Found ${data.loot.items.length} item(s)`, 'success');
          }
        };
        
        const handleSanctumExited = (): void => {
          console.log('🏛️ Exited sanctum');
          oceanScene.exitSanctum();
        };
        
        const handleSanctumReconnected = (data: { instanceId: string; sanctumGrade: string; currentRoomId: string; position: any }) => {
          console.log('🏛️ Reconnected to sanctum:', data.instanceId);
          oceanScene.enterSanctum(data.instanceId);
          // Request current room data
          socketRef.current?.emit('sanctum:request_room', {
            instanceId: data.instanceId,
            roomId: data.currentRoomId,
          });
        };

        const handleSanctumDungeonLayout = (data: { instanceId: string; rooms: any[]; corridors: any[]; entryRoomId: string; bossRoomId: string; overseerOfficeRoomId: string }) => {
          console.log('🏛️ Full dungeon layout received:', data.rooms.length, 'rooms,', data.corridors.length, 'corridors');
          try {
            oceanScene.renderSanctumDungeon(data);
            console.log('🏛️ renderSanctumDungeon called successfully');
          } catch (error) {
            console.error('🏛️ Error calling renderSanctumDungeon:', error);
          }
        };
        
        socketRef.current.on('sanctum:instance_created', handleSanctumInstanceCreated);
        socketRef.current.on('sanctum:dungeon_layout', handleSanctumDungeonLayout);
        socketRef.current.on('sanctum:room_data', handleSanctumRoomData);
        socketRef.current.on('sanctum:enemy_defeated', handleSanctumEnemyDefeated);
        socketRef.current.on('sanctum:loot_received', handleSanctumLootReceived);
        socketRef.current.on('sanctum:exited', handleSanctumExited);
        socketRef.current.on('sanctum:reconnected', handleSanctumReconnected);
        socketRef.current.on('sanctum:create_and_enter', handleSanctumCreateAndEnter);

        // Add to handlers for cleanup
        handlersRef.current.push(
          { event: 'sanctum:instance_created', handler: handleSanctumInstanceCreated },
          { event: 'sanctum:dungeon_layout', handler: handleSanctumDungeonLayout },
          { event: 'sanctum:room_data', handler: handleSanctumRoomData },
          { event: 'sanctum:enemy_defeated', handler: handleSanctumEnemyDefeated },
          { event: 'sanctum:loot_received', handler: handleSanctumLootReceived },
          { event: 'sanctum:exited', handler: handleSanctumExited },
          { event: 'sanctum:reconnected', handler: handleSanctumReconnected },
          { event: 'sanctum:create_and_enter', handler: handleSanctumCreateAndEnter }
        );
      }
    }

    // Cleanup function
    return () => {
      if (socketRef.current) {
        handlersRef.current.forEach(({ event, handler }) => {
          socketRef.current?.off(event, handler);
        });
        handlersRef.current = [];
        socketRef.current.off('territory:update', handleTerritoryUpdate);
        socketRef.current.disconnect();
      }
    };
  }, [
    socketRef,
    gameRef,
    // IMPORTANT: Removed playerInfo from dependency array to prevent socket reconnection
    // when world map opens/closes. We use initialPlayerInfoRef instead for playerJoin emit.
    setConnected,
    setPlayerId,
    setSocketForHotbar,
    setShipCount,
    setPosition,
    setTilePos,
    setChunkPos,
    setPlayerHp,
    setPlayerSP,
    setInCombat,
    setPieces,
    setXp,
    setLevel,
    setInfamy,
    setSecurityTier,
    setShipId,
    setShipType,
    setCurrentRegion,
    setIsDocked,
    setDockedIslandId,
    setUnlockedBlueprints,
    setHasEssenceEquipped,
    setSeaMonsters,
    setFactionReputation,
    setFactionTiers,
    setFactionDailyGains,
    setFactionDailyCaps,
    setResearchRefreshNonce,
    setKickMessage,
    setApiBaseUrl,
    setIsDisconnected,
    setReconnectAttempt,
    setDuplicateConnectionError,
    setCurrentWeather,
    setLoadingOverlayVisible,
    setLoadingOverlayMessage,
    setIsInstanceEntry,
    isDockedRef,
    dockedIslandIdRef,
    researchIslandIdRef,
    researchPanelOpenRef,
    hasReceivedLocalShipRef,
    phaserReadyRef,
    callReadyIfPossible,
    enqueueToast,
    addWorldEvent,
    getRaidTitle,
    getRaidFlavorText,
  ]);
}

